import { ActionEvents, ExternalConnection, ID } from '@griply/types';
import { getNewExternalConnections } from '@griply/utils';
import { useEffect } from 'react';

export type TrackExternalCalendarConnected = (
  event: ActionEvents.ExternalCalendarConnected,
  props: { provider: ExternalConnection['provider'] },
) => void;

export type TrackedIdsStorage = {
  read: (key: string) => ID[] | null;
  write: (key: string, ids: ID[]) => void;
};

export type UseTrackExternalCalendarConnectedOptions = {
  uid: ID | undefined;
  // undefined = query unresolved; collapsing it to [] emits every existing connection as fresh.
  connections: ExternalConnection[] | undefined;
  keyPrefix: string;
  storage: TrackedIdsStorage;
  track: TrackExternalCalendarConnected;
};

export const syncTrackedExternalConnections = ({
  uid,
  connections,
  keyPrefix,
  storage,
  track,
}: UseTrackExternalCalendarConnectedOptions) => {
  if (!uid || connections === undefined) {
    return;
  }
  const key = `${keyPrefix}${uid}`;
  const trackedIds = storage.read(key);
  const { newConnections, nextTrackedIds } = getNewExternalConnections(
    connections,
    trackedIds,
  );
  newConnections.forEach(({ provider }) =>
    track(ActionEvents.ExternalCalendarConnected, { provider }),
  );
  if (trackedIds === null || newConnections.length) {
    storage.write(key, nextTrackedIds);
  }
};

export const useTrackExternalCalendarConnected = (
  options: UseTrackExternalCalendarConnectedOptions,
) => {
  const { uid, connections, keyPrefix, storage, track } = options;

  useEffect(() => {
    syncTrackedExternalConnections({
      uid,
      connections,
      keyPrefix,
      storage,
      track,
    });
  }, [connections, uid, keyPrefix, storage, track]);
};
